import clsx from "clsx";
import { IListing } from "../../../interfaces";
import classes from "./ListingSelector.module.scss";
import { Button } from "@mui/material";
import { ChevronDownIcon } from "../../../../assets/icons/ChevronDownIcon";
import { ChevronUpIcon } from "../../../../assets/icons/ChevronUpIcon";
import { useEffect, useState } from "react";
import { Dialog } from "@mui/material";
import { SearchInput } from "../../DesktopHeader/components/SearchInput";
import { CloseIcon } from "../../../../assets/icons/CloseIcon";
import { AlertIcon } from "../../../../assets/icons/AlertIcon";
import { Checkbox } from "@mui/material";
import { getSelectedListingsSelector } from "@/redux/selectors/performancePageSelector";
import { useSelector } from "react-redux";

export const ListingSelector = ({
  listings,
  onSelect,
}: {
  listings: IListing[];
  onSelect: (listings: IListing[]) => void;
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const selectedStoreListings: IListing[] = useSelector(
    getSelectedListingsSelector
  );
  const [selectedListings, setSelectedListings] = useState<IListing[]>(() =>
    selectedStoreListings.filter((listing) => listing.is_listed)
  );
  const [allListing, setAllListing] = useState(listings);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [showMore, setShowMore] = useState(false);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;
  const icon = open ? <ChevronUpIcon /> : <ChevronDownIcon />;

  const onChange = (listing: IListing) => {
    setSelectedListings((prev) => {
      if (prev.includes(listing)) {
        return prev.filter(
          (id) => id.last_live_nick !== listing.last_live_nick
        );
      }
      return [...prev, listing];
    });
  };

  const setOnlyThisListing = (listing: IListing) => {
    const listings = [listing];
    setSelectedListings(listings);
  };

  const onChangeAll = () => {
    if (selectedListings.length === listings.length) {
      setSelectedListings([]);
    } else {
      setSelectedListings(listings);
    }
  };

  const removeListing = (listing: IListing) => {
    setSelectedListings(
      selectedListings.filter(
        (xListing) => xListing.last_live_nick !== listing.last_live_nick
      )
    );
  };

  const onSearchQueryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;

    setSearchQuery(query);
    setAllListing(
      listings.filter((listing) =>
        listing.last_live_nick.toLowerCase().includes(query.toLowerCase())
      )
    );
  };

  const onResetClick = () => {
    setSearchQuery("");
    setAllListing(listings);
    setSelectedListings([listings[0]]);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    setSelectedListings(selectedStoreListings);
  }, [selectedStoreListings]);

  return (
    <>
      <div className={classes.listingSelector}>
        <Button
          aria-describedby={id}
          className={clsx(classes.listingsButton, {
            [classes.expanded]: open,
          })}
          color="secondary"
          endIcon={icon}
          onClick={handleClick}
          variant="outlined"
        >
          {selectedListings.length > 1 ? (
            <div className={classes.listingWithNumber}>
              <div className={classes.listingWithNumberAmount}>
                {selectedListings.length}
              </div>
              <div className={classes.listingWithNumberText}>
                Properties Selected
              </div>
            </div>
          ) : selectedListings.length === 1 ? (
            <div>{selectedListings[0].property_id.trim().slice(0, 20)}</div>
          ) : (
            "Choose your listing"
          )}
        </Button>
      </div>
      <Dialog
        id={id}
        open={open}
        onClose={handleClose}
        classes={{
          paper: classes.popover,
        }}
        fullWidth
      >
        <div className={classes.listingsContainer}>
          <div className={classes.listingsHeader}>
            <div className={classes.listingsHeaderTitle}>
              Choose your listing
            </div>
            <CloseIcon color="#02094F" onClick={handleClose} />
          </div>
          <SearchInput value={searchQuery} onChange={onSearchQueryChange} />
          {selectedListings.length > 0 ? (
            <div className={classes.selectedListingsContainer}>
              <div className={classes.selectedListingsTitle}>
                Selected Properties
              </div>
              <div className={classes.selectedListings}>
                {(!showMore
                  ? selectedListings.slice(0, 2)
                  : selectedListings
                ).map((listing) => (
                  <div
                    className={classes.selectedListingName}
                    key={listing.last_live_nick}
                  >
                    <div>{listing.last_live_nick}</div>
                    <CloseIcon onClick={() => removeListing(listing)} />
                  </div>
                ))}
                {selectedListings.length > 2 && (
                  <div
                    className={classes.showMore}
                    onClick={() => setShowMore(!showMore)}
                  >
                    {showMore ? "See less" : "See more"}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className={classes.emptyMessage}>
              <AlertIcon />
              <div>Select at least one property</div>
            </div>
          )}
          <div className={classes.divider} />
          <div className={classes.totalCount}>
            Total{" "}
            {listings.length < 10 ? `0${listings.length}` : listings.length}
          </div>
          {searchQuery.length === 0 && (
            <div className={classes.listingItem} onClick={() => onChangeAll()}>
              <Checkbox
                className={classes.checkbox}
                checked={selectedListings.length === listings.length}
                inputProps={{ "aria-label": "controlled" }}
              />
              <p className={classes.listingName}>Select all</p>
            </div>
          )}
          {allListing.map((listing) => {
            return (
              <div
                className={classes.listingItem}
                key={listing.last_live_nick}
                onClick={() => onChange(listing)}
              >
                <Checkbox
                  className={classes.checkbox}
                  checked={selectedListings.some(
                    (xListing) =>
                      xListing.last_live_nick === listing.last_live_nick
                  )}
                  inputProps={{ "aria-label": "controlled" }}
                />
                <p className={classes.listingName}>{listing.last_live_nick}</p>
                <div
                  onClick={(event) => {
                    event.stopPropagation();
                    setOnlyThisListing(listing);
                  }}
                  className={classes.listingItemActions}
                >
                  Only this
                </div>
              </div>
            );
          })}
          <div className={classes.footer}>
            <Button
              className={classes.resetButton}
              variant="outlined"
              color="secondary"
              onClick={onResetClick}
            >
              Reset
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => onSelect(selectedListings)}
            >
              Select
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
};
